import { injectable } from 'inversify';
import { container, TYPES } from '../inversify.config';
import type { IMasterDataService, LocationOption } from '../interface/IMasterDataService';
import type { IHttpWrapperService } from '../interface/IHttpWrapperService';
import type { ApiResponse } from '@/shared/models/ApiResponse';

export class MasterDataService implements IMasterDataService {
  private httpService: IHttpWrapperService = container.get(TYPES.IHttpWrapperService);

  async GetMasterDataValues(fieldName: string, domainName: string, limit: number = 100, search: string = ''): Promise<ApiResponse<LocationOption[]>> {
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
    const response = await this.httpService.getRequest<LocationOption[]>(
      `masterdata/getvalue/${fieldName}?domainName=${encodeURIComponent(domainName)}&limit=${limit}${searchParam}`
    );
    
    return response.data;
  }

  async GetLocationValues(domainName: string, limit: number = 10, search: string = ''): Promise<ApiResponse<LocationOption[]>> {
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
    const response = await this.httpService.getRequest<LocationOption[]>(
      `masterdata/getvalue/location?domainName=${encodeURIComponent(domainName)}&limit=${limit}${searchParam}`
    );
    
    return response.data;
  }

  async GetRegionValues(domainName: string, limit: number = 100, search: string = ''): Promise<ApiResponse<LocationOption[]>> {
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
    const response = await this.httpService.getRequest<LocationOption[]>(
      `masterdata/getvalue/region?domainName=${encodeURIComponent(domainName)}&limit=${limit}${searchParam}`
    );
    
    return response.data;
  }

  async GetCountryCodeValues(domainName: string, limit: number = 100, search: string = ''): Promise<ApiResponse<LocationOption[]>> {
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
    const response = await this.httpService.getRequest<LocationOption[]>(
      `masterdata/getvalue/country_code?domainName=${encodeURIComponent(domainName)}&limit=${limit}${searchParam}`
    );
    
    return response.data;
  }

  async GetGeoHierarchyValues(domainName: string, limit: number = 100, search: string = ''): Promise<ApiResponse<LocationOption[]>> {
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
    const response = await this.httpService.getRequest<LocationOption[]>(
      `masterdata/getvalue/geo_hierarchy?domainName=${encodeURIComponent(domainName)}&limit=${limit}${searchParam}`
    );
    
    return response.data;
  }

  async GetMatchRuleProfileValues(domainName: string, limit: number = 100, search: string = ''): Promise<ApiResponse<LocationOption[]>> {
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
    const response = await this.httpService.getRequest<LocationOption[]>(
      `masterdata/getvalue/match_rule_profile?domainName=${encodeURIComponent(domainName)}&limit=${limit}${searchParam}`
    );
    
    return response.data;
  }

  async GetAllocationRuleProfileValues(domainName: string, limit: number = 100, search: string = ''): Promise<ApiResponse<LocationOption[]>> {
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
    const response = await this.httpService.getRequest<LocationOption[]>(
      `masterdata/getvalue/allocation_rule_profile?domainName=${encodeURIComponent(domainName)}&limit=${limit}${searchParam}`
    );
    
    return response.data;
  }

  async GetAutoApproveRuleProfileValues(domainName: string, limit: number = 100, search: string = ''): Promise<ApiResponse<LocationOption[]>> {
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
    const response = await this.httpService.getRequest<LocationOption[]>(
      `masterdata/getvalue/auto_approve_rule_profile?domainName=${encodeURIComponent(domainName)}&limit=${limit}${searchParam}`
    );
    
    return response.data;
  }

  async GetOutXmlProfileValues(domainName: string, limit: number = 100, search: string = ''): Promise<ApiResponse<LocationOption[]>> {
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
    const response = await this.httpService.getRequest<LocationOption[]>(
      `masterdata/getvalue/out_xml_profile?domainName=${encodeURIComponent(domainName)}&limit=${limit}${searchParam}`
    );
    
    return response.data;
  }

  async GetLineApproveTolProfileValues(domainName: string, limit: number = 100, search: string = ''): Promise<ApiResponse<LocationOption[]>> {
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
    const response = await this.httpService.getRequest<LocationOption[]>(
      `masterdata/getvalue/line_approve_tol_profile?domainName=${encodeURIComponent(domainName)}&limit=${limit}${searchParam}`
    );
    
    return response.data;
  }

  async GetScacValues(domainName: string, limit: number = 100, search: string = ''): Promise<ApiResponse<LocationOption[]>> {
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
    const response = await this.httpService.getRequest<LocationOption[]>(
      `masterdata/getvalue/scac?domainName=${encodeURIComponent(domainName)}&limit=${limit}${searchParam}`
    );
    
    return response.data;
  }

  async GetNmfcClassValues(domainName: string, limit: number = 100, search: string = ''): Promise<ApiResponse<LocationOption[]>> {
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
    const response = await this.httpService.getRequest<LocationOption[]>(
      `masterdata/getvalue/nmfc_class?domainName=${encodeURIComponent(domainName)}&limit=${limit}${searchParam}`
    );
    return response.data;
  }

  async GetEquipmentGroupValues(domainName: string, limit: number = 100, search: string = ''): Promise<ApiResponse<LocationOption[]>> {
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
    const response = await this.httpService.getRequest<LocationOption[]>(
      `masterdata/getvalue/equipment_group?domainName=${encodeURIComponent(domainName)}&limit=${limit}${searchParam}`
    );
    return response.data;
  }

  async GetTransportModeValues(domainName: string, limit: number = 100, search: string = ''): Promise<ApiResponse<LocationOption[]>> {
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
    const response = await this.httpService.getRequest<LocationOption[]>(
      `masterdata/getvalue/transport_mode?domainName=${encodeURIComponent(domainName)}&limit=${limit}${searchParam}`
    );
    return response.data;
  } 

  async GetGlKeyComponentTypeValues(domainName: string, limit: number = 100, search: string = ''): Promise<ApiResponse<LocationOption[]>> {
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
    const response = await this.httpService.getRequest<LocationOption[]>(
      `masterdata/getvalue/gl_key_component_type?domainName=${encodeURIComponent(domainName)}&limit=${limit}${searchParam}`
    );
    return response.data;
  }

  async GetGlKeyComponentParametersValues(domainName: string, limit: number = 100, search: string = ''): Promise<ApiResponse<LocationOption[]>> {
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
    const response = await this.httpService.getRequest<LocationOption[]>(
      `masterdata/getvalue/gl_key_component_parameters?domainName=${encodeURIComponent(domainName)}&limit=${limit}${searchParam}`
    );
    return response.data;
  }

  async GetSpecialServiceValues(domainName: string, limit: number = 100, search: string = ''): Promise<ApiResponse<LocationOption[]>> {
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
    const response = await this.httpService.getRequest<LocationOption[]>(
      `masterdata/getvalue/special_service?domainName=${encodeURIComponent(domainName)}&limit=${limit}${searchParam}`
    );
    return response.data;
  }

  async GetPackagingFormCodeValues(domainName: string, limit: number = 100, search: string = ''): Promise<ApiResponse<LocationOption[]>> {
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
    const response = await this.httpService.getRequest<LocationOption[]>(
      `masterdata/getvalue/packaging_form_code?domainName=${encodeURIComponent(domainName)}&limit=${limit}${searchParam}`
    );
    return response.data;
  }

  async GetDataQueryTypeValues(domainName: string, limit: number = 100, search: string = ''): Promise<ApiResponse<LocationOption[]>> {
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
    const response = await this.httpService.getRequest<LocationOption[]>(
      `masterdata/getvalue/data_query_type?domainName=${encodeURIComponent(domainName)}&limit=${limit}${searchParam}`
    );
    return response.data;
  }

  async GetTotalCostValues(domainName: string, limit: number = 100, search: string = ''): Promise<ApiResponse<LocationOption[]>> {
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
    const response = await this.httpService.getRequest<LocationOption[]>(
      `masterdata/getvalue/total_cost?domainName=${encodeURIComponent(domainName)}&limit=${limit}${searchParam}`
    );
    return response.data;
  }

  async GetExpectedCostCurrencyValues(domainName: string, limit: number = 100, search: string = ''): Promise<ApiResponse<LocationOption[]>> {
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
    const response = await this.httpService.getRequest<LocationOption[]>(
      `masterdata/getvalue/expected_cost_currency?domainName=${encodeURIComponent(domainName)}&limit=${limit}${searchParam}`
    );
    return response.data;
  }

  async GetShipUnitSpecValues(domainName: string, limit: number = 100, search: string = ''): Promise<ApiResponse<LocationOption[]>> {
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : '';
    const response = await this.httpService.getRequest<LocationOption[]>(
      `masterdata/getvalue/ship_unit_spec?domainName=${encodeURIComponent(domainName)}&limit=${limit}${searchParam}`
    );
    return response.data;
  }
}
